import React, { FC, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import styles from './UserPanelBox.module.css';
import wifiOffIcon from '@/assets/wifi-off.svg';
import { formatCurrency } from '@/pages/table/utils/utils';
import CurrencyIcon, { TetherIconColorOptions } from '@/components/CurrencySymbolMask/CurrencyIcon';
import CurrencyDisplay from './CurrencyDisplay';

interface UserPanelInfoBoxProps {
  alias: string;
  stack?: number;
  isActive?: boolean;
  label?: string;
  discardedCount?: number;
  isDisconnected?: boolean;
  isPlayersTurn: boolean;
  isCappedGame?: boolean;
  status: boolean;
  borderColor: string | null;
  joining: boolean;
  shouldDisplayInBB: boolean;
  bigBlind: number;
}

const UserPanelBox: FC<UserPanelInfoBoxProps> = ({
  alias,
  stack,
  isActive,
  label,
  status,
  isDisconnected = false,
  isCappedGame = false,
  isPlayersTurn,
  borderColor,
  joining,
  shouldDisplayInBB,
  bigBlind,
}) => {
  const ACTION_DISPLAY_TIMEOUT = 1000;

  const [displayLabel, setDisplayLabel] = useState<string | undefined>();
  const [messageContent, setMessageContent] = useState<string | undefined>();

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const playerFolded = displayLabel?.toLowerCase() === 'fold';
  const playerIsAllIn = status && stack === 0;

  useEffect(() => {
    const sittingOutMessage = 'Sitting out';
    const allInMessage = 'All In';
    const message = isDisconnected
      ? 'Connection issue'
      : !isActive
        ? sittingOutMessage
        : playerIsAllIn
          ? allInMessage
          : label || undefined;

    if (message) {
      setMessageContent(message);
      setDisplayLabel(label);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      if (message === sittingOutMessage || message === allInMessage) {
        return;
      }

      timerRef.current = setTimeout(() => {
        setMessageContent(undefined);
        setDisplayLabel(undefined);

        timerRef.current = null;
      }, ACTION_DISPLAY_TIMEOUT);
    }
    if (isActive && (messageContent === allInMessage || messageContent === sittingOutMessage)) {
      setMessageContent(undefined);
      setDisplayLabel(undefined);
    }
  }, [label, isActive, isDisconnected, playerIsAllIn]);

  const getMainBackground = () => {
    if (isDisconnected) {
      return 'bg-ph-orange-dark';
    }
    if (isPlayersTurn) {
      return 'bg-ph-orange-secondary';
    }
    if (playerFolded || !isActive) {
      return 'bg-ph-violet-medium';
    }
    return 'bg-phenom-background-gradient-gray-violet-darker';
  };

  const getLabelColor = () => {
    const backgroundColor = getMainBackground();
    if (backgroundColor === 'bg-ph-orange-dark' || backgroundColor === 'bg-ph-orange-secondary') {
      return 'text-white';
    }
    if (playerIsAllIn || backgroundColor === 'bg-phenom-background-gradient-gray-violet-darker') {
      return 'text-ph-orange';
    }
    return 'text-white';
  };

  const getCurrencyColor = () => {
    if (playerIsAllIn || isPlayersTurn) {
      return TetherIconColorOptions.white;
    }
    return;
  };

  const getAliasOpacity = () => {
    if (playerFolded) {
      return 'text-opacity-30';
    }
    if (isPlayersTurn) {
      return ''; //0 opacity for this case
    }
    return 'text-opacity-50';
  };

  return (
    <div
      className={twMerge(getMainBackground(), styles.mainContainer)}
      style={{
        borderColor: borderColor !== null ? borderColor : '',
      }}
    >
      {messageContent ? (
        <span className="text-center text-sm font-bold text-white text-opacity-30">
          {!isActive ? (
            <>
              {alias}
              <br /> {messageContent}
            </>
          ) : (
            <span className={getLabelColor()}>{messageContent}</span>
          )}
        </span>
      ) : (
        <>
          {
            <span className={twMerge('truncated text-center text-base font-semibold text-white', getAliasOpacity())}>
              {alias}
            </span>
          }
          {stack !== undefined && (
            <CurrencyDisplay
              stack={stack}
              isPlayersTurn={isPlayersTurn}
              getCurrencyColor={getCurrencyColor}
              formatCurrency={formatCurrency}
              CurrencyIcon={CurrencyIcon}
              isCappedGame={isCappedGame}
              playerIsAllIn={playerIsAllIn}
              joining={joining}
              shouldDisplayInBB={shouldDisplayInBB}
              bigBlind={bigBlind}
            />
          )}
        </>
      )}
      {isDisconnected && (
        <div className={styles.noWifiContainer}>
          <img src={wifiOffIcon} alt="no wifi icon" />
        </div>
      )}
    </div>
  );
};

export default UserPanelBox;
